// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DI_TOKENS, resolve } from '@src/di/container';
import {
  trackAddStoriesAbandoned,
  trackAddStoriesOpened,
} from '@src/analytics/EventTracker';

import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { DialogCard } from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';
import StatusMessageModal from '@src/components/common/StatusMessageModal';
import { UploadCredentialError } from '@src/services/upload';
import {
  useDatoClient,
  useDatoPerson,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';

import { StoryDetailsStep } from './StoryDetailsStep';
import { DEFAULT_STORY_TYPE, isIdLikeName } from './uploadCopy';
import { UploadStep } from './UploadStep';
import { useFolderPicker } from './useFolderPicker';
import { Step, useStoryCreator } from './useStoryCreator';

import type { ClassifiedUploadError } from '@src/services/upload/UploadErrorClassifier';
import type { Story } from '@src/types.ts/story';

const logger = LogManager.getLogger('component:AddStoriesModal');

const AddStoriesModal = observer(
  ({
    closeModal,
    onStoryBlankCreated,
    onStoryInitialized,
    onStoryCreated,
    onStoryProgress,
    onSave,
    modalId,
  }: {
    closeModal: (modalId: string, options?: { cancelUpload?: boolean }) => void;
    onStoryBlankCreated: (storyId: string, modalId: string) => void;
    onStoryInitialized: (s: Story, modalId: string) => void;
    onStoryCreated: (s: Story, modalId: string) => void;
    onStoryProgress: (percent: number, storyId?: string) => void;
    onSave?: () => void;
    modalId: string;
  }) => {
    const videoCreator = useVideoCreatorStore();
    const uploadFlow = useUserStoryUploadFlow();
    const userIdentity = useUserIdentity();
    const datoClientStore = useDatoClient();
    const datoPerson = useDatoPerson();
    const [isStatusMessageModalOpen, setIsStatusMessageModalOpen] =
      useState<boolean>(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [lastClassifiedError, setLastClassifiedError] =
      useState<ClassifiedUploadError | null>(null);
    const [isLeavePromptOpen, setIsLeavePromptOpen] = useState(false);
    const { organization } = videoCreator;
    // Read once: the entry point never changes for a dialog's lifetime.
    const [entryPoint] = useState(() => uploadFlow.getEntryPoint(modalId));

    const {
      showFolderPicker,
      folderOptions,
      selectedFolderId,
      setSelectedFolderId,
      assignStoryToFolder,
    } = useFolderPicker(organization?.id);

    const storyCreatorParams = useMemo(
      () => ({
        videoCreator,
        organization: videoCreator.organization,
        datoClientStore,
        userIdentity,
        datoPersonId: datoPerson?.person?.id,
        modalId,
        onStoryBlankCreated: (storyId: string) =>
          onStoryBlankCreated(storyId, modalId),
        onStoryInitialized: (s: Story) => {
          onStoryInitialized(s, modalId);
          assignStoryToFolder(s.id);
        },
        onStoryCreated: (s: Story) => onStoryCreated(s, modalId),
        onStoryProgress: onStoryProgress,
        onError: (error: unknown, classified?: ClassifiedUploadError) => {
          setLastClassifiedError(classified ?? null);
          setStatusMessage(
            classified?.userMessage ??
              (error instanceof UploadCredentialError
                ? error.userMessage
                : null),
          );
          setIsStatusMessageModalOpen(true);
        },
        storyCreationService: resolve(DI_TOKENS.STORY_CREATION_SERVICE),
        entryPoint,
      }),
      [
        videoCreator,
        videoCreator.organization,
        datoClientStore,
        userIdentity,
        datoPerson?.person?.id,
        onStoryBlankCreated,
        onStoryInitialized,
        onStoryCreated,
        modalId,
        onStoryProgress,
        organization,
        assignStoryToFolder,
        entryPoint,
      ],
    );

    const {
      step,
      goToNextStep,
      canGoToStep,
      handleStoryVideoUpload,
      handleStoryUrlUpload,
      retryUpload,
      uploadMechanism,
      storyName,
      setStoryName,
      storyLanguage,
      setStoryLanguage,
      storyType,
      setStoryType,
      videoMusicStrategy,
      setVideoMusicStrategy,
      audioEnhancementDisabled,
      storyArtifactsFiles,
      storyArtifactsVideoFiles,
      maxUploadSizeGB,
      maxStoryTitleLength,
      setZoomRecordingData,
      continueInBackground,
      removeUpload,
    } = useStoryCreator(storyCreatorParams);

    const prevArtifactsCountRef = useRef<number>(0);
    const prevArtifactsVideoCountRef = useRef<number>(0);

    // Navigate to Story Manager (or other destination) when save is triggered
    useEffect(() => {
      if (step === Step.five && onSave) {
        // Defer to avoid triggering navigation during render cycle
        const id = setTimeout(onSave, 0);
        return () => clearTimeout(id);
      }
    }, [step, onSave]);

    // Instrument artifact additions (doc + video) by detecting count increases
    useEffect(() => {
      prevArtifactsCountRef.current = storyArtifactsFiles.length;
    }, [storyArtifactsFiles, videoCreator, uploadMechanism]);

    useEffect(() => {
      prevArtifactsVideoCountRef.current = storyArtifactsVideoFiles.length;
    }, [storyArtifactsVideoFiles, videoCreator, uploadMechanism]);

    // The hook pre-fills the story name from the file name. A name that is
    // only an id (a UUID, "IMG_4521") is a worse default than an empty field,
    // so the first name a file pick produces is checked once (PLAT-11942).
    const checkPrefilledNameRef = useRef(false);
    const [nameWasCleared, setNameWasCleared] = useState(false);
    useEffect(() => {
      if (!checkPrefilledNameRef.current || !storyName) return;
      checkPrefilledNameRef.current = false;
      if (isIdLikeName(storyName)) {
        setStoryName('');
        setNameWasCleared(true);
      }
    }, [storyName]);

    // Story Details is one screen over the hook's two steps: choosing a story
    // type is all Step.two asks, so it moves on to Step.three by itself and
    // Save then runs the existing Step.three -> Step.five transition.
    // The story type is pre-answered with the common case (raw), so a person
    // who agrees only presses Save.
    useEffect(() => {
      if (step !== Step.two) return;
      if (!storyType) setStoryType(DEFAULT_STORY_TYPE);
      else goToNextStep();
    }, [step, storyType]);

    const handleVideoFiles = (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      if (files[0].size > maxUploadSizeGB * 1000 * 1000 * 1000) {
        logger.warn('story-video-too-large', {
          modalId,
          maxUploadSizeGB,
          fileSize: files[0].size,
        });
        setStatusMessage(
          `File size exceeds the maximum upload size of ${maxUploadSizeGB} GB`,
        );
        setIsStatusMessageModalOpen(true);
        return;
      }
      checkPrefilledNameRef.current = true;
      handleStoryVideoUpload(files);
    };

    // Opened by a page drop (PLAT-11945): start that file exactly as the drop
    // box would, so the dialog lands on Story Details already uploading.
    useEffect(() => {
      trackAddStoriesOpened({ entry_point: entryPoint });
      const files = uploadFlow.takeInitialFiles(modalId);
      if (files?.length) handleVideoFiles(files);
    }, []);

    const renderStep = () => {
      switch (step) {
        case Step.one:
          return (
            <UploadStep
              handleStoryVideoUpload={handleVideoFiles}
              handleStoryUrlUpload={handleStoryUrlUpload}
              setStoryName={setStoryName}
              setZoomRecordingData={setZoomRecordingData}
              maxUploadSizeGB={maxUploadSizeGB}
            />
          );
        case Step.two:
        case Step.three:
          return (
            <StoryDetailsStep
              storyType={storyType}
              setStoryType={setStoryType}
              videoMusicStrategy={videoMusicStrategy}
              setVideoMusicStrategy={setVideoMusicStrategy}
              audioEnhancementDisabled={audioEnhancementDisabled}
              storyName={storyName}
              setStoryName={setStoryName}
              nameWasCleared={nameWasCleared}
              maxStoryTitleLength={maxStoryTitleLength}
              storyLanguage={storyLanguage}
              setStoryLanguage={setStoryLanguage}
              allowLangSelect={
                videoCreator.organization?.allowLangSelectAtUserStoryUpload
              }
              showFolderPicker={showFolderPicker}
              folderOptions={folderOptions}
              selectedFolderId={selectedFolderId}
              setSelectedFolderId={setSelectedFolderId}
              canSave={step === Step.three && canGoToStep(Step.five)}
              onSave={goToNextStep}
            />
          );
        default:
          return null;
      }
    };

    const handleCloseModal = () => {
      // The upload kicks off in Step One and keeps running in the background
      // after the modal closes. Fire the save/navigation callback so closing
      // the modal early (e.g. skipping the folder step) still lands the user in
      // the Story Manager to watch progress — the store defers the save until
      // the story id exists when the close races blank-story creation. Being
      // past Step One is what marks an upload as initiated (only the upload
      // handlers advance the wizard); dismissing at Step One stays put.
      if (step !== Step.one) {
        onSave?.();
      }
      if (step !== Step.five) {
        trackAddStoriesAbandoned({
          entry_point: entryPoint,
          step: step === Step.one ? 'upload' : 'story_details',
          upload_started: step !== Step.one,
        });
      }
      closeModal(modalId);
    };

    // Past Step One an upload is running, and only Step.five starts its
    // processing — so a close asks whether to keep or remove it (PLAT-10748).
    const handleCloseRequest = () => {
      if (step === Step.one) {
        handleCloseModal();
        return;
      }
      setIsLeavePromptOpen(true);
    };

    const handleRemoveVideo = () => {
      setIsLeavePromptOpen(false);
      trackAddStoriesAbandoned({
        entry_point: entryPoint,
        step: 'story_details',
        upload_started: true,
      });
      removeUpload();
      closeModal(modalId, { cancelUpload: true });
    };

    if (isStatusMessageModalOpen) {
      return (
        <StatusMessageModal
          enabled
          variant="user-upload-failed"
          customMessage={statusMessage}
          onClose={handleCloseModal}
          retryable={lastClassifiedError?.retryable}
          onRetry={
            lastClassifiedError?.retryable
              ? () => {
                  setIsStatusMessageModalOpen(false);
                  setLastClassifiedError(null);
                  retryUpload();
                }
              : undefined
          }
        />
      );
    }

    if (step === Step.five) {
      return null;
    }

    return (
      <>
        <Modal
          isOpen
          closeModal={handleCloseRequest}
          disableEscapeClose={isLeavePromptOpen}
        >
          <DialogCard $size="md" data-testid="add-stories-modal">
            {renderStep()}
          </DialogCard>
        </Modal>
        {isLeavePromptOpen && (
          <ConfirmModal
            title="Keep This Story?"
            message="Continue and it finishes in the background, or remove it."
            cancelText="Remove Video"
            confirmText="Continue Upload"
            accentColor="var(--primary)"
            closeModal={() => setIsLeavePromptOpen(false)}
            onCancel={handleRemoveVideo}
            onConfirm={() => {
              setIsLeavePromptOpen(false);
              continueInBackground();
            }}
          />
        )}
      </>
    );
  },
);

export default AddStoriesModal;
